import type { PopperProps as MuiPopperProps } from "@mui/material/Popper";
import { type DataGridProps, useGridApiRef } from "@mui/x-data-grid";
import { useEffect, useMemo, useRef } from "react";

// `BasePopper` forwards `popperOptions` to `@mui/material/Popper` at runtime,
// but MUI X's abstract `PopperProps` doesn't type it. Augment so TS accepts it.
declare module "@mui/x-data-grid" {
	interface BasePopperPropsOverrides {
		popperOptions?: MuiPopperProps["popperOptions"];
	}
}

type SlotProps = NonNullable<DataGridProps["slotProps"]>;
type PanelSlotProps = NonNullable<SlotProps["panel"]>;

/**
 * Anchors the column menu + filter panel under the clicked column. Works around
 * two `@mui/x-data-grid@^9` internals — revisit on any major upgrade:
 *
 * 1. `.MuiDataGrid-root` has an identity `transform` that breaks Popper's
 *    `position: absolute` offset-parent math; `strategy: "fixed"` sidesteps it.
 * 2. The filter panel anchors to one grid-level element regardless of which
 *    column triggered it. We capture the column header on `menuOpen` and pass
 *    it as the panel's anchor instead.
 *
 * `gridReady` (= the live grid is mounted, i.e. not loading/error) is a re-run trigger:
 * `useGridApiRef()` is `useRef(null)` and is only bound once a DataGrid using it mounts. A grid that
 * first renders in its loading state shows a skeleton grid with no `apiRef`, so a one-shot mount
 * subscription would bail on the null api and never attach. Re-running when the real grid mounts
 * fixes the panel never anchoring on grids that start loading.
 *
 * `externalApiRef` lets a caller own the ref (so the SAME grid instance powers both panel-anchoring
 * and imperative APIs like `scrollToIndexes`). Hooks can't be called conditionally, so the internal
 * `useGridApiRef()` still runs; we simply prefer the external ref when one is supplied.
 */
export function useColumnAnchoredPanel(
	gridReady: boolean,
	externalApiRef?: ReturnType<typeof useGridApiRef>,
): { apiRef: ReturnType<typeof useGridApiRef>; slotProps: SlotProps } {
	const internalApiRef = useGridApiRef();
	const apiRef = externalApiRef ?? internalApiRef;
	const lastColumnHeaderRef = useRef<HTMLElement | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `gridReady` is a deliberate re-run trigger, not read in the body — when the grid transitions loading → ready, apiRef.current is finally bound and the effect must re-run to subscribe (a one-shot mount subscription would bail on the null api).
	useEffect(() => {
		const api = apiRef.current;
		if (!api) return;
		return api.subscribeEvent("menuOpen", (params) => {
			const target = params.target as HTMLElement | null;
			lastColumnHeaderRef.current = target?.closest<HTMLElement>(".MuiDataGrid-columnHeader") ?? null;
		});
	}, [apiRef, gridReady]);

	return useMemo<{ apiRef: ReturnType<typeof useGridApiRef>; slotProps: SlotProps }>(
		() => ({
			apiRef,
			slotProps: {
				basePopper: { popperOptions: { strategy: "fixed" } },
				// `target` accepts a function at runtime (`Popper`'s `anchorEl`) though typed as
				// `Element | null`. `placement: "bottom"` wins over `GridPanel`'s default because
				// it spreads slot props after its own placement.
				panel: {
					target: (() => lastColumnHeaderRef.current ?? undefined) as unknown as Element | null,
					placement: "bottom",
				} as PanelSlotProps,
			},
		}),
		[apiRef],
	);
}
